/**
 * services/passengers.js — threads as passengers on milestones (2026-09-09; docs/PASSENGERS.md).
 *
 * Daily triage of the threads in your court (the gate), pairing a thread to a milestone so it rides
 * that plane, the passengers on a milestone (the posting block reads this), and the tower's moves.
 * All against api/passengers.js. Table ids and field names live server-side.
 */

async function post(body) {
  const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
  const res = await fetch('/api/passengers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`)
  return data
}

/** The gate: my open, unpaired threads, with the milestones they could ride. */
// The browser's own time zone rides along: "morning" is the person's morning (2026-10-15).
function myTz() { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { return '' } }
export function getTriageStatus(memberId) { return post({ action: 'status', memberId, tz: myTz() }) }

/**
 * One triage decision. decision: 'close' (information only) | 'done' (did it, closing) |
 * 'pair' (+ milestoneId) | 'new' (+ newMilestone: { typeId, latestFinish?, days?, phaseId?, subtitle? }).
 */
export function decideThread(memberId, memberName, threadId, decision, extra = {}) {
  return post({ action: 'decide', memberId, memberName, threadId, decision, tz: myTz(), ...extra })
}

/** Tell a handed-off thread's followers something happened to it (a note, closed, reopened).
 *  Best-effort: never throws, does nothing when nobody handed the thread off. */
export function notifyThreadFollowers(threadId, memberId, memberName, event, note) {
  return post({ action: 'threadEvent', threadId, memberId, memberName, event, note }).catch(() => null)
}

export function snoozeTriage(memberId, hours = 1) { return post({ action: 'snooze', memberId, hours }) }
/** Answer one thread in a phase-end sweep (api/_phaseSweep.js): decision 'got' | 'notNeeded' | 'waiting' | 'ours'. */
export function sweepDecide(memberId, memberName, { phaseId, projectId, threadId, decision, note }) {
  return post({ action: 'sweepDecide', memberId, memberName, phaseId, projectId, threadId, decision, note })
}
/** Answer a client's revision request on posted minutes: 'revised' (and reposted) | 'noChange'. */
export function resolveMinutesRevision(memberName, { responseId, decision, note }) {
  return post({ action: 'minutesRevision', memberName, responseId, decision, note })
}
/** The Kit Coordinator's answer to one kit item (AlderFLOW §25): inHand | notRequired | chased. */
export function decideKitItem(memberId, memberName, { itemId, decision, note, expectedOn }) {
  return post({ action: 'kitDecide', memberId, memberName, itemId, decision, note, expectedOn })
}
/** Outside-court chases (database/330): the coordinator's and the creator's answers.
 *  decision: discussed | chased | cameBack | snooze (snoozeFor: day | week | 3weeks) | close,
 *  and the creator's go | mine (askCreator is retired; rows already asked still finish). */
export function decideOutsideChase(memberId, memberName, { threadId, decision, note, expectedOn, snoozeFor }) {
  return post({ action: 'outsideChase', memberId, memberName, threadId, decision, note, expectedOn, snoozeFor, tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return null } })() })
}
/** The Permitting Coordinator's hearing cards (api/_hearings.js, docs/HEARING_OUTCOMES.md).
 *  recordHearingOutcome: what the board decided, the vote, conditions, the board, and the two
 *  emails exactly as the coordinator read them. Returns { report: [lines], appealEndsOn }. */
export function recordHearingOutcome(memberId, memberName, outcome) {
  return post({ action: 'hearingOutcome', memberId, memberName, ...outcome })
}
/** The hearing card for one agency review, from the permit tracker (no triage needed). { card } | { none } */
export function getHearingCardForReview(reviewId) {
  return post({ action: 'hearingCardForReview', reviewId })
}
/** The appeal period ended. decision: none | filed (note required). */
export function recordAppealOutcome(memberId, memberName, { outcomeId, decision, note, appealHearingDate }) {
  return post({ action: 'appealOutcome', memberId, memberName, outcomeId, decision, note, appealHearingDate })
}
/** Thread numbers from one counter (database/331): `count` consecutive numbers for a project. */
export function claimThreadNumbers(projectId, count = 1) {
  return post({ action: 'claimThreadNumbers', projectId, count }).then(r => (r && r.numbers) || [])
}
/** Admin: pause the triage lockout for the rest of today (it ends at midnight Pacific). */
export function pauseTriageEnforcement() { return post({ action: 'pauseEnforcement' }) }
export function resumeTriageEnforcement() { return post({ action: 'resumeEnforcement' }) }

/** Open passengers on these milestones: { byMilestone: { <msId>: [{ threadId, title, type, ... }] } }. */
export function getPassengersFor(milestoneIds) { return post({ action: 'forMilestones', milestoneIds }) }

/** Which of these threads ride a milestone: { byThread: { <threadId>: { milestoneId, milestoneName } } }. */
export function getRidingFor(threadIds) { return post({ action: 'forThreads', threadIds }) }

/** The crew asking the tower to move a passenger off this plane. */
export function requestPassengerMove(memberId, memberName, threadId, note) {
  return post({ action: 'requestMove', memberId, memberName, threadId, note })
}

/** The crew moving a passenger to another unposted milestone on the same project, no tower needed. */
export function crewMovePassenger(memberId, memberName, threadId, toMilestoneId, note) {
  return post({ action: 'crewMove', memberId, memberName, threadId, toMilestoneId, note })
}

/**
 * Candidates in SCHEDULE order (Matt, 2026-10-26): by the project's phase order (phases are
 * passed in schedule order, e.g. ctx.state.phases), then by scheduled start. Phases not in the
 * list go last, ordered by start. Returns { value, label } options for the searchable Select.
 */
export function scheduleOrderedOptions(cands, phases, milestones) {
  const idx = new Map((phases || []).map((p, i) => [p.id, i]))
  const rank = c => (c.phaseId && idx.has(c.phaseId)) ? idx.get(c.phaseId) : 9999
  // Undated milestones (most of Construction Support) keep the outline's own order.
  const pos = new Map((milestones || []).map((m, i) => [m.id, i]))
  const at = c => pos.has(c.milestoneId) ? pos.get(c.milestoneId) : 99999
  return (cands || []).slice()
    .sort((a, b) => (rank(a) - rank(b))
      || String(a.earliestStart || '9999').localeCompare(String(b.earliestStart || '9999'))
      || (at(a) - at(b)))
    .map(c => ({ value: c.milestoneId, label: (c.inAir ? '✈ ' : '') + (c.caLabel || c.name) + (c.phaseName ? ' · ' + c.phaseName.replace(/\s*Phase$/, '') : '') }))
}

/** Open Active milestones on a project, in-air first. */
export function getCandidateMilestones(projectId) { return post({ action: 'candidates', projectId }) }

/* Tower only */
export function getTowerTriageList() { return post({ action: 'towerList' }) }
/** toMilestoneId null = back to triage (unpaired). */
export function movePassenger(memberId, memberName, threadId, toMilestoneId, note) {
  return post({ action: 'move', memberId, memberName, threadId, toMilestoneId, note })
}
export function setThreadCourt(memberId, memberName, threadId, courtMemberId) {
  return post({ action: 'setCourt', memberId, memberName, threadId, courtMemberId })
}

/** Which milestone type (by deliverable-type id — 2026-09-22, was matched by Name) a thread of
 *  this kind usually rides when it needs a new plane. Thread `type` keys are a stored Airtable
 *  singleSelect, stable regardless of this rename pass — only the milestone-type VALUES needed
 *  converting. */
export const DEFAULT_NEW_TYPE_BY_THREAD_TYPE = {
  'RFIs': 'rec2YwWMVUNjaExbb',              // RFI Response
  'Submittals': 'recql3s0G1jaNV2AO',        // Submittal Review
  'Design Direction': 'recxNKoZtc9WlqCqF',  // Client Requested Design Revision - Concept
  'Design Approvals': 'recxNKoZtc9WlqCqF',  // Client Requested Design Revision - Concept
  'Owner Items': 'recYJ26aU8cDuiTib',       // Client Requested Design Revision - Drawings
  'Permitting Items': 'recO3wapjkgPiP4C9',  // Plan Check Resubmission 🔹
  'Inspector Items': 'recd3EN9Ccp3MWuH3',   // Architects Supplemental Instructions
  'Contractor Items': 'recd3EN9Ccp3MWuH3',  // Architects Supplemental Instructions
}
export const DEFAULT_DAYS_BY_TYPE_ID = {
  'rec2YwWMVUNjaExbb': 1,   // RFI Response
  'recql3s0G1jaNV2AO': 2,   // Submittal Review
}

/** A new RFI or submittal's thread, riding the project's RFI Support / Submittal Support milestone
 *  (created the first time it's needed). Returns { threadId, milestoneId }. */
export function rideSupport(projectId, kind, title, description, memberName) {
  return post({ action: 'supportThread', projectId, kind, title, description, memberName })
}
